import { Container, Paper } from '@mui/material'
import { useState, useMemo, createContext, useContext, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import LoadingSpinner from '../../../../custom-components/LoadingSpinner'
import DragDropLayout from '../components/DragDropLayout'
import useTileQuery, { useModalDashboardTileQuery } from '../hooks/useTileQuery'
import { isEmpty, isEqual } from 'lodash'

const DashboardContext = createContext()

function DashboardProvider(props) {
	const [selectedFields, setSelectedFields] = useState({})
	const [currentSelected, setCurrentSelected] = useState('')
	const [isRemovedTile, setIsRemovedTile] = useState({})
	const [linkedTitle, setLinkedTitle] = useState({})

	// for dataGridTile { [datagridKey] : mobileUnitNumber}
	const [selectedMobileUnitNumber, setSelectedMobileUnitNumber] = useState({})

	// for feedTile { [mobileUnitKey] : mobileUnitNumber}
	const [selectedMobileUnitForFeedTile, setSelectedMobileUnitForFeedTile] = useState({})

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleSelectRow = (tileName, value) => {
		if (selectedFields[tileName] === value) {
			// delete key if value is the same
			const { [tileName]: _, ...rest } = selectedFields
			setSelectedFields(rest)
			return
		}
		setSelectedFields({ ...selectedFields, [tileName]: value })
	}

	const handleSelectMobileUnitForDataGridTile = (dataGridTileName, value) => {
		if (isEmpty(dataGridTileName)) return
		// const mobileNumber = value.number
		if (isEqual(selectedMobileUnitNumber[dataGridTileName], value)) {
			// delete key if value is the same
			const { [dataGridTileName]: _, ...rest } = selectedMobileUnitNumber
			setSelectedMobileUnitNumber(rest)
			return
		}
		setSelectedMobileUnitNumber({ ...selectedMobileUnitNumber, [dataGridTileName]: value })
	}

	const handleSelectMobileUnitForFeedTile = (mobileUnitTileKey, value) => {
		if (isEmpty(mobileUnitTileKey)) return
		// const mobileNumber = value.number
		if (isEqual(selectedMobileUnitForFeedTile[mobileUnitTileKey], value)) {
			// delete key if value is the same
			const { [mobileUnitTileKey]: _, ...rest } = selectedMobileUnitForFeedTile
			setSelectedMobileUnitForFeedTile(rest)
			return
		}
		setSelectedMobileUnitForFeedTile({
			...selectedMobileUnitForFeedTile,
			[mobileUnitTileKey]: value,
		})
	}

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleSelectCurrent = (value) => {
		setCurrentSelected(value)
	}

	function resetSelectedMobileNumber() {
		setSelectedMobileUnitNumber({})
	}

	function onSelectMobileUnit(mobileUnitTileKey, dataGridTileName, value) {
		handleSelectMobileUnitForDataGridTile(dataGridTileName, value)
		handleSelectRow(mobileUnitTileKey, value)
		handleSelectMobileUnitForFeedTile(mobileUnitTileKey, value)
	}

	function onSetLinkedTitle(dataGridTileName, value) {
		if (isEmpty(dataGridTileName)) return
		if (isEqual(linkedTitle[dataGridTileName], value)) return
		setLinkedTitle({ ...linkedTitle, [dataGridTileName]: value })
	}

	function removeSelectedRowOfTile(tileName) {
		if (isEmpty(tileName)) return
		const { [tileName]: _, ...rest } = selectedFields
		setSelectedFields(rest)
	}

	const value = useMemo(
		() => ({
			selectedFields,
			onSelectRow: handleSelectRow,
			currentSelected,
			onSelectCurrent: handleSelectCurrent,
			isRemovedTile,
			setIsRemovedTile,
			selectedMobileUnitNumber,
			selectedMobileUnitForFeedTile,
			onSelectMobileUnit,
			resetSelectedMobileNumber,
			linkedTitle,
			onSetLinkedTitle,
			removeSelectedRowOfTile,
		}),
		[
			selectedFields,
			handleSelectRow,
			currentSelected,
			handleSelectCurrent,
			isRemovedTile,
			selectedMobileUnitNumber,
		]
	)
	return <DashboardContext.Provider value={value} {...props} />
}

export function useTileDashboard() {
	const context = useContext(DashboardContext)
	if (!context) {
		throw new Error('useTileDashboard must be used within a DashboardProvider')
	}
	return context
}

const DashboardLayout = (props) => {
	const { id: dashboardKey } = useParams()

	const { blockedTileListWithPosition, isLoading: isTileLoading } = useTileQuery({
		dashboardKey: dashboardKey,
	})

	if (isTileLoading) return <LoadingSpinner />

	if (blockedTileListWithPosition.length === 0) return null

	return (
		<DashboardProvider>
			<Container maxWidth={false} sx={{ my: 1, px: '8px !important' }}>
				<Paper elevation={3} sx={{ width: '100%', minHeight: '80dvh' }}>
					{blockedTileListWithPosition?.length > 0 && (
						<DragDropLayout layout={blockedTileListWithPosition} isOnDashboard />
					)}
				</Paper>
			</Container>
		</DashboardProvider>
	)
}

export const ModalDashboardLayoutContent = (props) => {
	const { passedIdValue, modalDashboardSetting } = props

	const { blockedTileListWithPosition, isLoading: isTileLoading } = useModalDashboardTileQuery({
		dashboardKey: modalDashboardSetting?.dashboardKey,
	})

	if (isTileLoading) return <LoadingSpinner />

	if (blockedTileListWithPosition.length === 0) return null

	return (
		<DashboardProvider>
			<Container maxWidth={false} sx={{ my: 1, px: '8px !important' }}>
				<Paper elevation={3} sx={{ width: '100%', minHeight: '80dvh' }}>
					{blockedTileListWithPosition?.length > 0 && (
						<DragDropLayout
							layout={blockedTileListWithPosition}
							isMainDashboard={false}
							passedIdValue={passedIdValue}
							modalDashboardSetting={modalDashboardSetting}
							isOnDashboard
						/>
					)}
				</Paper>
			</Container>
		</DashboardProvider>
	)
}

export default DashboardLayout
